import { useEffect, useRef } from 'react';
import { Button, ListBox, ListBoxItem, Popover, Select, SelectValue } from 'react-aria-components';
import { controlClass, FieldErrorText, FieldHint, FieldLabel, fieldClassName } from './Field';

export interface SelectOption {
  key: string;
  label: string;
}

export interface SelectFieldProps {
  label: string;
  hint?: string;
  error?: string;
  options: SelectOption[];
  value: string;
  onChange: (key: string) => void;
  className?: string;
}

export function SelectField({ label, hint, error, options, value, onChange, className }: SelectFieldProps) {
  // react-aria's trigger button drops aria-invalid, so mirror the error onto it.
  const triggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const el = triggerRef.current;
    if (!el) return;
    if (error) el.setAttribute('aria-invalid', 'true');
    else el.removeAttribute('aria-invalid');
  }, [error]);

  return (
    <Select
      selectedKey={value}
      onSelectionChange={(key) => {
        if (key !== null) onChange(String(key));
      }}
      isInvalid={!!error}
      className={fieldClassName(className)}
    >
      <FieldLabel>{label}</FieldLabel>
      <Button ref={triggerRef} className={`${controlClass} flex items-center justify-between gap-2 text-left cursor-pointer`}>
        <SelectValue className="m-0 p-0 font-ui text-md text-ink" />
        <span aria-hidden="true" className="m-0 p-0 text-xs text-muted">
          ▾
        </span>
      </Button>
      <FieldHint hint={hint} />
      <FieldErrorText error={error} />
      <Popover className="m-0 p-1 bg-surface border border-line rounded-control shadow-popover">
        <ListBox className="m-0 p-0 border-0 bg-surface font-ui text-md text-ink outline-none">
          {options.map((o) => (
            <ListBoxItem
              key={o.key}
              id={o.key}
              textValue={o.label}
              className="m-0 px-2 py-1 font-ui text-md text-ink bg-surface cursor-pointer outline-none data-[focused]:bg-panel data-[selected]:font-semibold"
            >
              {o.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  );
}
